import React, { FC, useState, useCallback } from "react";

import { useUpdateApplicationMutation, useGetAppInfoQuery, UpdateAppInput } from "generated/graphql";
import { wrapUrqlMutation } from "utils/graphql";
import { ClientApp } from "models/App";

import { AvailabilityStep, AvailabilityStepProps } from "../../components/setup/AvailabilityStep";

export interface AvailabilityStepContainerProps extends Omit<AvailabilityStepProps, "onNext" | "app"> {
    app?: Pick<ClientApp, "availability" | "appId" | "organizationId">;
    onNext(): void;
    onBack?(): void;
}

export const AvailabilityStepContainer: FC<AvailabilityStepContainerProps> = (props) => {
    const { app, onNext, onBack, ...restProps } = props;

    const [saving, setSaving] = useState(false);

    // Get the app data to pre-populate the form
    const [getAppInfo] = useGetAppInfoQuery({
        pause: !app?.appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId: app?.appId || "",
        },
    });

    const { fetching: loading, stale: isStale } = getAppInfo;

    // Setup updateApplication mutation
    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
    });

    const handleNext = useCallback(
        async (appData: Pick<ClientApp, "availability" | "appId" | "organizationId">) => {
            setSaving(true);

            try {
                // Transform availability data to match the expected schema structure
                // The UpdateAppInput expects the availability in a specific format
                const updateAppInput: UpdateAppInput = {
                    availability: appData.availability ? {
                        availableDays: appData.availability.availableDays,
                        defaultBusyDays: appData.availability.defaultBusyDays ? {
                            availableDays: appData.availability.defaultBusyDays.availableDays,
                            blockCurrentDay: appData.availability.defaultBusyDays.blockCurrentDay,
                            blockNextBusinessDays: appData.availability.defaultBusyDays.blockNextBusinessDays,
                            currentDayAvailableUntil: appData.availability.defaultBusyDays.currentDayAvailableUntil,
                        } : undefined,
                    } : {},
                };

                // Update the app with availability settings
                if (appData?.appId) {
                    console.log("Updating availability with data:", updateAppInput);
                    await updateApplication({
                        appId: appData.appId,
                        app: updateAppInput,
                    });
                } else {
                    console.warn("No app ID available, skipping availability update.");
                }

                // Success - proceed to next step
                onNext();
            } catch (err) {
                console.error("Failed to update availability:", err);
                // Still proceed to next step even if save fails - we don't want to block the user
                onNext();
            } finally {
                setSaving(false);
            }
        },
        [onNext, updateApplication]
    );

    return (
        <AvailabilityStep
            {...restProps}
            app={app}
            onNext={handleNext}
            onBack={onBack}
            saving={saving}
            loading={loading || isStale}
        />
    );
};
